<template>
  <v-dialog v-model="visible" max-width="900px" persistent scrollable>
    <v-card>
      <v-toolbar dense flat color="primary" dark>
        <v-toolbar-title class="text-subtitle-1 font-weight-bold">
          <v-icon left small>{{ esEdicion ? 'mdi-pencil' : esDuplicado ? 'mdi-content-copy' : 'mdi-plus' }}</v-icon>
          {{
            esEdicion
              ? 'Editar Área en Lista de Precio'
              : esDuplicado
              ? 'Duplicar Área en Lista de Precio'
              : 'Agregar Área a Lista de Precio'
          }}
        </v-toolbar-title>
        <v-spacer />
        <v-btn icon small dark @click="cerrar" :disabled="enviando">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-toolbar>

      <v-card-text class="pa-4" style="max-height: 70vh">
        <v-form ref="formulario" @submit.prevent="guardar">
          <v-row dense>
            <v-col cols="12" md="6">
              <v-select
                v-model="form.areaUniversidadId"
                :items="areasUniversidad"
                item-text="nombre"
                item-value="id"
                label="Área Universidad"
                outlined
                dense
                :loading="cargandoSelects"
                :disabled="esEdicion"
                :rules="[(v) => !!v || 'El área es requerida']"
                @change="onAreaChange">
                <template v-slot:prepend-inner>
                  <v-icon small class="mt-1">mdi-map-marker</v-icon>
                </template>
              </v-select>
            </v-col>
            <v-col cols="12" md="6">
              <v-select
                v-model="form.modalidad"
                :items="modalidades"
                label="Modalidad"
                outlined
                dense
                :disabled="esEdicion || !areaRequiereModalidad"
                :rules="[(v) => !!v || 'La modalidad es requerida']">
                <template v-slot:selection="{ item }">
                  <v-chip x-small label :color="item === 'teorico' ? 'indigo lighten-4' : 'teal lighten-4'">{{
                    item
                  }}</v-chip>
                </template>
              </v-select>
            </v-col>
          </v-row>

          <!-- Conceptos -->
          <div class="d-flex align-center mb-2 mt-1">
            <v-icon x-small color="grey darken-1" class="mr-1">mdi-tag-multiple</v-icon>
            <span class="text-caption font-weight-bold grey--text text--darken-2">CONCEPTOS</span>
            <v-chip x-small label class="ml-2" color="grey lighten-2">{{ form.conceptos.length }}</v-chip>
            <v-spacer />
            <v-btn v-if="!esEdicion" x-small color="primary" outlined @click="agregarConcepto">
              <v-icon x-small left>mdi-plus</v-icon>
              Agregar Concepto
            </v-btn>
          </div>

          <v-alert v-if="form.conceptos.length === 0" type="info" dense text class="mb-2">
            Sin conceptos. Haga clic en "Agregar Concepto".
          </v-alert>

          <!-- Card por cada concepto -->
          <v-card v-for="(concepto, cIdx) in form.conceptos" :key="cIdx" outlined class="mb-3 pa-3">
            <div class="d-flex align-center mb-2">
              <span class="text-caption font-weight-bold grey--text">Concepto #{{ cIdx + 1 }}</span>
              <v-spacer />
              <v-btn v-if="!esEdicion" icon x-small color="red" @click="eliminarConcepto(cIdx)">
                <v-icon x-small>mdi-close</v-icon>
              </v-btn>
            </div>

            <v-row dense>
              <v-col cols="12" md="3">
                <v-select
                  v-model="concepto.conceptoPagoId"
                  :items="conceptosPagoFiltrados(cIdx)"
                  item-text="nombre"
                  item-value="id"
                  label="Concepto Pago"
                  outlined
                  dense
                  :disabled="esEdicion"
                  :rules="[(v) => !!v || 'Requerido']" />
              </v-col>
              <v-col cols="12" md="3">
                <v-text-field
                  v-model.number="concepto.montoCuota"
                  label="Monto Cuota / Módulo"
                  outlined
                  dense
                  type="number"
                  min="0"
                  :rules="[(v) => v > 0 || 'Mayor a 0']" />
              </v-col>
              <v-col cols="12" md="3">
                <v-text-field
                  v-model.number="concepto.numeroCuotas"
                  label="N° Cuotas / Módulos"
                  outlined
                  dense
                  type="number"
                  min="1"
                  :rules="[(v) => v >= 1 || 'Mínimo 1']" />
              </v-col>
              <v-col cols="12" md="3">
                <v-sheet rounded class="pa-2 d-flex align-center justify-center primary lighten-5" style="height: 40px">
                  <span class="text-caption grey--text text--darken-1 mr-1">TOTAL</span>
                  <span class="text-subtitle-2 font-weight-bold primary--text">
                    {{ calcularPrecioTotal(concepto) }}
                  </span>
                </v-sheet>
              </v-col>
            </v-row>

            <!-- Modalidades de pago del concepto -->
            <v-row dense>
              <!-- Contado -->
              <v-col cols="12" md="6">
                <v-card outlined class="pa-2" :style="{ borderLeft: '3px solid #388E3C' }">
                  <div class="d-flex align-center mb-2">
                    <v-icon x-small color="green darken-2" class="mr-1">mdi-cash</v-icon>
                    <span class="text-caption font-weight-bold green--text text--darken-2">CONTADO</span>
                    <v-spacer />
                    <v-switch
                      v-model="concepto.tieneContado"
                      dense
                      hide-details
                      class="mt-0 pt-0"
                      @change="toggleModalidad(concepto, 'contado')" />
                  </div>
                  <template v-if="concepto.tieneContado">
                    <v-text-field
                      v-model.number="concepto.contadoPorcentaje"
                      label="% Máx. Descuento"
                      outlined
                      dense
                      hide-details="auto"
                      type="number"
                      min="0"
                      max="100"
                      suffix="%"
                      class="mb-1" />
                    <v-sheet rounded class="pa-1 text-center green lighten-5">
                      <span class="text-caption grey--text text--darken-1">Mínimo: </span>
                      <span class="font-weight-bold green--text text--darken-2">
                        {{ calcularPrecioMinimo(concepto, concepto.contadoPorcentaje) }}
                      </span>
                    </v-sheet>
                  </template>
                </v-card>
              </v-col>

              <!-- Crédito -->
              <v-col cols="12" md="6">
                <v-card outlined class="pa-2" :style="{ borderLeft: '3px solid #1976D2' }">
                  <div class="d-flex align-center mb-2">
                    <v-icon x-small color="blue darken-2" class="mr-1">mdi-credit-card</v-icon>
                    <span class="text-caption font-weight-bold blue--text text--darken-2">CRÉDITO</span>
                    <v-spacer />
                    <v-switch
                      v-model="concepto.tieneCredito"
                      dense
                      hide-details
                      class="mt-0 pt-0"
                      @change="toggleModalidad(concepto, 'credito')" />
                  </div>
                  <template v-if="concepto.tieneCredito">
                    <v-text-field
                      v-model.number="concepto.creditoPorcentaje"
                      label="% Máx. Descuento"
                      outlined
                      dense
                      hide-details="auto"
                      type="number"
                      min="0"
                      max="100"
                      step="0.01"
                      suffix="%"
                      class="mb-1" />
                    <v-sheet rounded class="pa-1 text-center blue lighten-5">
                      <span class="text-caption grey--text text--darken-1">Mínimo: </span>
                      <span class="font-weight-bold blue--text text--darken-2">
                        {{ calcularPrecioMinimo(concepto, concepto.creditoPorcentaje) }}
                      </span>
                    </v-sheet>
                  </template>
                </v-card>
              </v-col>
            </v-row>
          </v-card>

          <v-card v-if="form.conceptos.length > 0" outlined class="pa-2">
            <v-row dense>
              <v-col cols="12" md="6">
                <v-sheet
                  rounded
                  class="pa-2 my-1 d-flex align-center justify-center green lighten-5"
                  style="height: 40px">
                  <span class="text-caption grey--text text--darken-1 mr-1">TOTAL</span>
                  <span class="text-subtitle-2 font-weight-bold green--text text--darken-2">
                    {{ calcularTotalPorModalidad('contado') }}
                  </span>
                </v-sheet>

                <v-sheet
                  rounded
                  class="pa-2 my-1 d-flex align-center justify-center green lighten-5"
                  style="height: 40px">
                  <span class="text-caption grey--text text--darken-1 mr-1">TOTAL MÍNIMO</span>
                  <span class="text-subtitle-2 font-weight-bold green--text text--darken-2">
                    {{ calcularPrecioTotalMinimoPorModalidad('contado') }}
                  </span>
                </v-sheet>
              </v-col>

              <v-col cols="12" md="6">
                <v-sheet
                  rounded
                  class="pa-2 my-1 d-flex align-center justify-center primary lighten-5"
                  style="height: 40px">
                  <span class="text-caption grey--text text--darken-1 mr-1">TOTAL</span>
                  <span class="text-subtitle-2 font-weight-bold primary--text">
                    {{ calcularTotalPorModalidad('credito') }}
                  </span>
                </v-sheet>

                <v-sheet
                  rounded
                  class="pa-2 my-1 d-flex align-center justify-center primary lighten-5"
                  style="height: 40px">
                  <span class="text-caption grey--text text--darken-1 mr-1">TOTAL MÍNIMO</span>
                  <span class="text-subtitle-2 font-weight-bold primary--text">
                    {{ calcularPrecioTotalMinimoPorModalidad('credito') }}
                  </span>
                </v-sheet>
              </v-col>
            </v-row>
          </v-card>
        </v-form>

        <v-alert v-if="mensajeError" type="error" dense text class="mt-3 mb-0">
          {{ mensajeError }}
        </v-alert>
      </v-card-text>

      <v-divider />

      <v-card-actions class="px-4 py-3">
        <v-spacer />
        <v-btn text color="grey darken-1" @click="cerrar" :disabled="enviando">Cancelar</v-btn>
        <v-btn color="primary" :loading="enviando" @click="guardar">
          <v-icon left small>mdi-check</v-icon>
          Guardar
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import ListaPrecioService from '../../services/lista-precio.service';
import AreaUniversidadService from '../../services/area-universidad.service';
import ConceptosPagosService from '../../services/conceptos-pagos.service';

const AREAS_CON_MODALIDAD = ['INGENIERÍA', 'SALUD'];

export default {
  name: 'FormularioAreaListaPrecio',
  props: {
    universidadId: {
      type: Number,
      default: null,
    },
  },
  data() {
    return {
      visible: false,
      enviando: false,
      esDuplicado: false,
      esEdicion: false,
      areaOriginal: null,
      cargandoSelects: false,
      mensajeError: '',
      listaPrecioId: null,
      tipoListaPrecioId: null,
      detallesExistentes: [],
      form: {
        areaUniversidadId: null,
        modalidad: 'teorico',
        conceptos: [],
      },
      areasUniversidad: [],
      conceptosPago: [],
      modalidades: ['teorico', 'practico'],
    };
  },
  computed: {
    areaNombre() {
      if (!this.form.areaUniversidadId) return '';
      const found = this.areasUniversidad.find((a) => a.id === this.form.areaUniversidadId);
      return found ? found.nombre.toUpperCase() : '';
    },
    areaRequiereModalidad() {
      return AREAS_CON_MODALIDAD.some((a) => this.areaNombre.includes(a));
    },
  },
  methods: {
    conceptosPagoFiltrados(currentIdx) {
      const usados = this.form.conceptos
        .filter((_, i) => i !== currentIdx)
        .map((c) => c.conceptoPagoId)
        .filter(Boolean);
      return this.conceptosPago.filter((cp) => !usados.includes(cp.id));
    },
    async abrir(item, areaDuplicar = null, { modo = 'agregar' } = {}) {
      this.mensajeError = '';
      this.listaPrecioId = item.id;
      this.tipoListaPrecioId = item.tipoListaPrecio.id;
      this.detallesExistentes = [];

      this.esEdicion = modo === 'editar';
      this.esDuplicado = modo === 'duplicar' || (!!areaDuplicar && modo !== 'editar');
      this.areaOriginal = areaDuplicar || null;
      if (areaDuplicar) {
        this.form = this.convertirAreaResumenAForm(areaDuplicar);
      } else {
        this.form = {
          areaUniversidadId: null,
          modalidad: 'teorico',
          conceptos: [],
        };
      }

      this.visible = true;
      this.cargarSelects();
      this.cargarDetallesExistentes();

      this.$nextTick(() => {
        this.$refs.formulario?.resetValidation();
      });
    },
    convertirAreaResumenAForm(area) {
      const creditoMod = (area.modalidades || []).find((m) => m.modalidadPago === 'credito');
      const contadoMod = (area.modalidades || []).find((m) => m.modalidadPago === 'contado');

      // Agrupar conceptos por conceptoPago.id
      const conceptosMap = {};

      if (creditoMod) {
        for (const con of creditoMod.conceptos) {
          const id = con.conceptoPago.id;
          if (!conceptosMap[id]) {
            conceptosMap[id] = {
              conceptoPagoId: id,
              montoCuota:
                Number(con.numeroCuotas) > 0
                  ? Math.round((Number(con.monto) / Number(con.numeroCuotas)) * 100) / 100
                  : 0,
              numeroCuotas: Number(con.numeroCuotas),
              tieneCredito: false,
              creditoPorcentaje: 0,
              creditoDetalleListaPrecioId: null,
              creditoDetalleListaPrecioModalidadId: null,
              tieneContado: false,
              contadoPorcentaje: 0,
              contadoDetalleListaPrecioId: null,
              contadoDetalleListaPrecioModalidadId: null,
            };
          }
          conceptosMap[id].tieneCredito = true;
          conceptosMap[id].creditoPorcentaje = Number(con.porcentajeMaximoDescuento) || 0;
          conceptosMap[id].creditoDetalleListaPrecioId = con.detalleListaPrecioId || null;
          conceptosMap[id].creditoDetalleListaPrecioModalidadId = con.detalleListaPrecioModalidadId || null;
        }
      }

      if (contadoMod) {
        for (const con of contadoMod.conceptos) {
          const id = con.conceptoPago.id;
          if (!conceptosMap[id]) {
            conceptosMap[id] = {
              conceptoPagoId: id,
              montoCuota:
                Number(con.numeroCuotas) > 0
                  ? Math.round((Number(con.monto) / Number(con.numeroCuotas)) * 100) / 100
                  : 0,
              numeroCuotas: Number(con.numeroCuotas),
              tieneCredito: false,
              creditoPorcentaje: 0,
              creditoDetalleListaPrecioId: null,
              creditoDetalleListaPrecioModalidadId: null,
              tieneContado: false,
              contadoPorcentaje: 0,
              contadoDetalleListaPrecioId: null,
              contadoDetalleListaPrecioModalidadId: null,
            };
          }
          conceptosMap[id].tieneContado = true;
          conceptosMap[id].contadoPorcentaje = Number(con.porcentajeMaximoDescuento) || 0;
          conceptosMap[id].contadoDetalleListaPrecioId = con.detalleListaPrecioId || null;
          conceptosMap[id].contadoDetalleListaPrecioModalidadId = con.detalleListaPrecioModalidadId || null;
        }
      }

      return {
        areaUniversidadId: area.areaUniversidad.id,
        modalidad: area.modalidad || 'teorico',
        conceptos: Object.values(conceptosMap),
      };
    },
    cerrar() {
      this.visible = false;
      this.mensajeError = '';
    },
    async cargarSelects() {
      const params = { universidadId: this.universidadId };
      this.cargandoSelects = true;

      try {
        const [areasRes, conceptosRes] = await Promise.all([
          AreaUniversidadService.findAll({ params }),
          ConceptosPagosService.findAll({ params }),
        ]);

        this.areasUniversidad = areasRes.data.datos || [];
        this.conceptosPago = conceptosRes.data.datos || [];
      } catch (error) {
        this.$notify({
          group: 'notifications',
          title: 'Error',
          text: 'Error al cargar datos de selección',
          type: 'error',
        });
      } finally {
        this.cargandoSelects = false;
      }
    },
    async cargarDetallesExistentes() {
      try {
        const { data } = await ListaPrecioService.findOne(this.listaPrecioId);
        this.detallesExistentes = data.datos?.detalles || data.detalles || [];
      } catch (error) {
        this.mensajeError = 'Error al cargar detalles existentes de la lista';
      }
    },
    onAreaChange() {
      if (!this.areaRequiereModalidad) {
        this.form.modalidad = 'teorico';
      }
    },
    agregarConcepto() {
      this.form.conceptos.push({
        conceptoPagoId: null,
        montoCuota: 0,
        numeroCuotas: 1,
        tieneCredito: false,
        creditoPorcentaje: 0,
        tieneContado: false,
        contadoPorcentaje: 0,
      });
    },
    eliminarConcepto(index) {
      this.form.conceptos.splice(index, 1);
    },
    toggleModalidad(concepto, tipo) {
      if (tipo === 'credito' && !concepto.tieneCredito) {
        concepto.creditoPorcentaje = 0;
      }
      if (tipo === 'contado' && !concepto.tieneContado) {
        concepto.contadoPorcentaje = 0;
      }
    },
    calcularPrecioTotal(concepto) {
      return Math.round((concepto.montoCuota || 0) * (concepto.numeroCuotas || 0) * 100) / 100;
    },
    calcularPrecioMinimo(concepto, porcentaje) {
      const total = this.calcularPrecioTotal(concepto);
      if (!porcentaje || porcentaje <= 0) return total;
      return Math.round((total - (total * porcentaje) / 100) * 100) / 100;
    },
    calcularTotalPorModalidad(modalidad) {
      let total = 0;

      for (const concepto of this.form.conceptos) {
        const tieneModalidad = modalidad === 'credito' ? concepto.tieneCredito : concepto.tieneContado;
        if (tieneModalidad) {
          total += this.calcularPrecioTotal(concepto);
        }
      }

      return Math.round(total * 100) / 100;
    },
    calcularPrecioTotalMinimoPorModalidad(modalidad) {
      let total = 0;

      for (const concepto of this.form.conceptos) {
        const tieneModalidad = modalidad === 'credito' ? concepto.tieneCredito : concepto.tieneContado;
        const porcentaje = modalidad === 'credito' ? concepto.creditoPorcentaje : concepto.contadoPorcentaje;

        if (tieneModalidad) {
          total += this.calcularPrecioMinimo(concepto, porcentaje);
        }
      }

      return Math.round(total * 100) / 100;
    },
    formConceptosADetalles() {
      const detalles = [];

      for (const concepto of this.form.conceptos) {
        const modalidadesPagos = [];

        if (concepto.tieneCredito) {
          const modPago = {
            modalidadPago: 'credito',
            porcentajeMaximoDescuento: concepto.creditoPorcentaje || 0,
            precioTotalMinimo: this.calcularPrecioMinimo(concepto, concepto.creditoPorcentaje),
          };
          if (this.esEdicion && concepto.creditoDetalleListaPrecioModalidadId) {
            modPago.id = concepto.creditoDetalleListaPrecioModalidadId;
          }
          modalidadesPagos.push(modPago);
        }

        if (concepto.tieneContado) {
          const modPago = {
            modalidadPago: 'contado',
            porcentajeMaximoDescuento: concepto.contadoPorcentaje || 0,
            precioTotalMinimo: this.calcularPrecioMinimo(concepto, concepto.contadoPorcentaje),
          };
          if (this.esEdicion && concepto.contadoDetalleListaPrecioModalidadId) {
            modPago.id = concepto.contadoDetalleListaPrecioModalidadId;
          }
          modalidadesPagos.push(modPago);
        }

        const detalle = {
          areaUniversidadId: this.form.areaUniversidadId,
          conceptoPagoId: concepto.conceptoPagoId,
          monto: this.calcularPrecioTotal(concepto),
          numeroCuotas: concepto.numeroCuotas,
          modalidad: this.form.modalidad,
          modalidadesPagos,
        };

        if (this.esEdicion) {
          const detalleId = concepto.creditoDetalleListaPrecioId || concepto.contadoDetalleListaPrecioId;
          if (detalleId) {
            detalle.id = detalleId;
          }
        }

        detalles.push(detalle);
      }

      return detalles;
    },
    async guardar() {
      if (!this.$refs.formulario.validate()) return;

      if (this.form.conceptos.length === 0) {
        this.mensajeError = 'Debe agregar al menos un concepto';
        return;
      }

      this.enviando = true;
      this.mensajeError = '';

      try {
        const nuevosDetalles = this.formConceptosADetalles();
        let detallesBase = this.detallesExistentes;

        if (this.esEdicion && this.areaOriginal) {
          detallesBase = detallesBase.filter(
            (d) =>
              !(
                d.areaUniversidadId === this.areaOriginal.areaUniversidad.id &&
                d.modalidad === this.areaOriginal.modalidad
              ),
          );
        }

        const todosDetalles = [...detallesBase, ...nuevosDetalles];

        await ListaPrecioService.update(this.listaPrecioId, {
          tipoListaPrecioId: this.tipoListaPrecioId,
          detalles: todosDetalles,
        });

        this.$notify({
          group: 'notifications',
          title: 'Lista de Precio',
          text: this.esEdicion ? 'Área editada correctamente' : 'Área agregada correctamente',
          type: 'success',
        });

        this.$emit('guardado');
        this.cerrar();
      } catch (error) {
        const data = error?.response?.data;
        this.mensajeError = data?.mensaje || error.message || 'Error al guardar';
      } finally {
        this.enviando = false;
      }
    },
  },
};
</script>
